import { Grid, Stack } from '@mui/material';
import CustomButton from 'components/Common/Button';
import TextInput from 'components/Common/TextInput';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

export interface InviteMemberFormValues {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

export interface InviteMemberFormProps {
    loading?: boolean;
    onSubmit: (values: InviteMemberFormValues) => void;
}

const formSchema = yup
    .object({
        firstName: yup.string().required('First name is required').trim().min(1).max(100),
        lastName: yup.string().required('Last name is required').trim().min(1).max(100),
        email: yup
            .string()
            .required('A valid email is required')
            .email('A valid email is required')
            .lowercase()
            .trim()
            .max(200, 'Email must be at most 200 characters'),
        phone: yup
            .string()
            .required('Phone number is required')
            .trim()
            .min(7, 'Phone number must be at least 7 characters')
            .max(20, 'Phone number must be at most 20 characters'),
    })
    .required();

const InviteMemberForm = ({ loading, onSubmit }: InviteMemberFormProps) => {
    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitted, touchedFields },
    } = useForm<InviteMemberFormValues>({
        resolver: yupResolver(formSchema),
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
        },
    });

    const fieldError = (name: keyof InviteMemberFormValues) => {
        const touched = touchedFields[name];
        if (!isSubmitted && !touched) return undefined;
        return errors[name];
    };

    const fieldHelper = (name: keyof InviteMemberFormValues) => {
        const err = fieldError(name);
        const showSlot = isSubmitted || Boolean(touchedFields[name]);
        return {
            helperText: err?.message ?? (showSlot ? '\u00a0' : undefined),
            FormHelperTextProps: {
                sx: {
                    marginTop: '4px',
                    marginLeft: 0,
                    minHeight: showSlot ? '1.25rem' : 0,
                },
            },
        };
    };

    const submit = (data: InviteMemberFormValues) => {
        onSubmit({
            firstName: data.firstName.trim(),
            lastName: data.lastName.trim(),
            email: data.email.trim().toLowerCase(),
            phone: data.phone.trim(),
        });
    };

    return (
        <form onSubmit={handleSubmit(submit)} noValidate>
            <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                    <Controller
                        name="firstName"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                width="100%"
                                placeholder="First name"
                                label="First Name"
                                icon={null}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={Boolean(fieldError('firstName'))}
                                {...fieldHelper('firstName')}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                    <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                width="100%"
                                icon={null}
                                placeholder="Last name"
                                label="Last Name"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={Boolean(fieldError('lastName'))}
                                {...fieldHelper('lastName')}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                icon={null}
                                width="100%"
                                placeholder="Username@email.com"
                                label="Email"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={Boolean(fieldError('email'))}
                                {...fieldHelper('email')}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                icon={null}
                                width="100%"
                                placeholder="Phone number"
                                label="Phone Number"
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                error={Boolean(fieldError('phone'))}
                                {...fieldHelper('phone')}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <Stack justifyContent="center" direction="row" sx={{ mt: { xs: 2, sm: 3 } }}>
                <CustomButton title="Create Member" type="submit" loading={loading} sx={{ width: { xs: '100%', sm: 'min(240px, 60%)' } }} />
            </Stack>
        </form>
    );
};

export default InviteMemberForm;
